import React, { MutableRefObject, useContext, useEffect, useRef } from 'react';

import { ShowDialog } from '@/store/dialog/actions';
import { ShowDialogParams } from '@/store/dialog/types';
import { replace } from 'redux-first-history';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export type HistoryBackActionCallback = () => boolean;
export interface HistoryBackAction {
    pushBackCallback: (callback?: HistoryBackActionCallback) => void;
    popBackCallback: () => void;
    updatePathSafely: (path: string) => void;
    popAndExecuteBackCallback: () => void;
}

interface HistoryBackActionProps {
    isSavedRef?: MutableRefObject<boolean>;
    exitModalTitle?: string;
    exitModalBody?: string | JSX.Element;
}

export const useHistoryBackAction = ({
    isSavedRef,
    exitModalTitle,
    exitModalBody,
}: HistoryBackActionProps): HistoryBackAction => {
    const actions = useRef<HistoryBackActionCallback[]>([]);
    const actionsCount = useRef(0);
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const location = useLocation();
    const exitConfirmed = useRef(false);
    const isGoingForward = useRef(false);
    const isPathUpdated = useRef(false);

    const onPopState = (event: any) => {
        if (isGoingForward.current) {
            isGoingForward.current = false;
            return;
        }
        if (
            isSavedRef?.current === false &&
            actions.current.length === 0 &&
            actionsCount.current === 0 &&
            !exitConfirmed.current
        ) {
            isGoingForward.current = true;
            window.history.forward();
            handleExit();
            return;
        }
        if (actions.current.length < actionsCount.current || event?.singleSpaTrigger === 'replaceState') {
            return;
        }
        if (actions.current.length > 0) {
            let callbackResultSuccess;
            try {
                callbackResultSuccess = actions.current[actions.current.length - 1]?.();
            } catch (e) {
                console.error('History callback error', e.message);
            }
            if (callbackResultSuccess) {
                actions.current.pop();

                setTimeout(() => {
                    actionsCount.current -= 1;
                }, 10);
            }
            isGoingForward.current = true;
            window.history.forward();
        }
    };

    const pushBackCallback = (callback?: HistoryBackActionCallback) => {
        actionsCount.current += 1;
        setTimeout(() => {
            actions.current.push(callback);
        }, 10);
    };

    const popBackCallback = (): void => {
        actions.current.pop();
        actionsCount.current -= 1;
    };
    const popAndExecuteBackCallback = (): void => {
        const callback = actions.current.pop();
        actionsCount.current -= 1;
        callback();
    };
    const updatePathSafely = (path: string): void => {
        if (location.pathname === path) {
            return;
        }
        isGoingForward.current = true;
        isPathUpdated.current = true;
        dispatch(replace(path));
    };

    const handleExit = () => {
        if (!isSavedRef || isSavedRef.current) {
            return;
        }
        const params: ShowDialogParams = {
            title: exitModalTitle || t('confirm'),
            cancelLabel: t('cancel'),
            confirmLabel: t('confirm'),
            body: exitModalBody,
            onClickConfirm: () => {
                exitConfirmed.current = true;
                window.history.go(isPathUpdated.current ? -3 : -2);
            },
        };
        dispatch(ShowDialog(params));
    };

    useEffect((): (() => void) => {
        window.history.pushState(null, null, window.location.href);

        setTimeout(() => {
            window.addEventListener('popstate', onPopState);
        }, 0);
        return () => {
            window.removeEventListener('popstate', onPopState);
        };
    }, []);

    return { pushBackCallback, popBackCallback, updatePathSafely, popAndExecuteBackCallback };
};

export const HistoryBackActionContext = React.createContext<HistoryBackAction>(null);

export const useHistoryBackActionContext = (): HistoryBackAction => {
    const context = useContext(HistoryBackActionContext);
    if (!context) {
        console.error('HistoryBackActionContext not found. Used outsiede of the provider. ');
        return null;
    }
    return context;
};
